// Помощник: спросить про деньги словами и получить число из базы.
//
// ## Почему это ОКНО, а не строка в одном разделе
//
// До 25.08.2026 строка вопроса стояла внутри «Дел» и больше нигде. Из шести остальных
// разделов помощника не было видно вовсе — то есть человек, который живёт в «Деньгах», о нём
// просто не знал. `ПРАВИЛА.md` (раздел 1.1) при этом объявляет помощника ЕДИНСТВЕННОЙ своей
// поверхностью в вебе, вызываемой по Ctrl+K.
//
// Теперь поверхность одна — окно, — а входов в неё два: сочетание клавиш и кнопка «Спросить»
// в полосе разделов, видная с любого экрана. Сочетание без кнопки было бы тайным знанием:
// финансовый директор не догадается нажать Ctrl+K, если об этом нигде не написано.
//
// ## Почему ответ показывается вместе с величинами и ссылкой
//
// Сумма, которую нечем проверить, бесполезна: человек либо идёт пересчитывать руками — и
// помощник не сэкономил ничего, либо принимает на веру — и однажды ошибается вместе с ним.
// Поэтому под фразой всегда стоят величины, из которых она сложена, и переход на экран, где
// те же числа видно глазами.
//
// ## Почему видно, кто сложил фразу
//
// Когда фразу собрал код, а не модель, это написано прямо. Так человек знает, чему доверяет:
// числа считает код всегда, а вот формулировка бывает машинной. Молчание об этом — способ
// однажды получить претензию «ваш робот сказал мне неправду».
//
// ## Примеры — это кнопки, а не текст, 25.08.2026
//
// Раньше под полем стояла серая строка «Например: сколько денег в полях · кто не отчитывается»
// и не делала ничего. Человеку, который не знает, о чём тут можно спросить, она предлагала
// перепечатать пример руками. Теперь это нажимаемые примеры: один щелчок — и вопрос задан.
// Поле ввода и кнопка взяты из дизайн-системы, а не набраны своей вёрсткой: своя кнопка
// отличалась от всех остальных кнопок продукта высотой и радиусом.

import { RiArrowRightSLine, RiSparkling2Line } from '@remixicon/react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Input from '@/shared/ui/input';

import { подсказкаЗапроса } from '@/shared/api/endpoints/assistant';

import { useAsk, useAssistantQueries } from './queries';

/** Пока список едет либо не приехал — честная строка, а не выдуманные примеры. */
function подписьПокаЕдет(едет: boolean) {
  return (
    <span className='text-paragraph-xs text-text-sub-600'>
      {едет
        ? 'Смотрим, о чём вы можете спросить…'
        : 'Список вопросов не приехал. Спросить всё равно можно — напишите словами.'}
    </span>
  );
}

export function AssistantBar({
  начальныйВопрос,
  наПереход,
}: {
  начальныйВопрос?: string;
  /** Позвать при переходе на экран: окно обязано закрыться, а не остаться поверх него. */
  наПереход?: () => void;
}) {
  const [вопрос, задатьВопрос] = useState(начальныйВопрос ?? '');
  // Прошлые вопросы этого же человека, свежий первым.
  //
  // ⚠️ Без них разговора нет НИГДЕ: сервер умеет достраивать месяц и фамилию из сказанного
  // раньше, но получает он их от клиента. «Сколько висит на Петрове» → «а у Иванова?» без
  // этого списка остаётся непонятым, хотя сервер понять уже умеет.
  const [прошлые, запомнить] = useState<string[]>([]);
  const [замечание, поставитьЗамечание] = useState<string | null>(null);
  const спросить = useAsk();
  // О чём можно спросить — с сервера и ПО ПРАВАМ. Зашитые в веб три фразы предлагали одно и
  // то же всем, включая тех, кому такой ответ не положен: охранник видел «сколько к выплате
  // за май» и получал отказ на собственный вопрос.
  const умеет = useAssistantQueries();
  // Порядок приходит с сервера уже осмысленным: вперёд то, на что сегодня есть ответ. Своей
  // сортировки здесь нет намеренно — она разъехалась бы с телефонами.
  const подсказки = (умеет.data?.items ?? [])
    .map(подсказкаЗапроса)
    .filter((текст): текст is string => текст !== null)
    // Шесть, а не пятнадцать: ряд из пятнадцати кнопок перестаёт быть подсказкой и становится
    // оглавлением, которое надо изучать. Остальное человек спросит своими словами.
    .slice(0, 6);
  // Образец в поле ввода — живой, а не выдуманный. До 08.09.2026 здесь стоял «Петров»,
  // которого в реестре заказчика нет вовсе: человек повторял образец и получал «не нашла».
  const плейсхолдер = подсказки[0]
    ? `Спросите про деньги: ${подсказки[0]}`
    : 'Спросите про деньги своими словами';

  const отправить = (текст: string = вопрос) => {
    const очищенный = текст.trim();
    if (очищенный.length === 0) {
      // Гейт объясняет причину, а не молчит выключенной кнопкой (правило интерфейса 10).
      поставитьЗамечание('напишите вопрос: например, «сколько денег в полях»');
      return;
    }
    if (спросить.isPending) {
      // Второе нажатие, пока едет ответ, — это второй запрос за тем же самым. Кнопка при этом
      // не выключается: выключенная кнопка молчит о причине, а правило интерфейса 10 требует
      // объяснения.
      поставитьЗамечание('уже считаю, ответ вот-вот придёт');
      return;
    }
    поставитьЗамечание(null);
    спросить.mutate({ question: очищенный, history: прошлые });
    запомнить((было) => [очищенный, ...было].slice(0, 5));
  };

  // Вопрос, набранный в палитре, задаётся сам — человек его уже написал и уже нажал Enter.
  // Требовать нажать «Спросить» второй раз значило бы спросить «вы уверены?» на действие,
  // которое ничего не портит и отменяется закрытием окна.
  //
  // ## Почему через таймер, а не прямо в эффекте
  //
  // Это не украшение и не «на всякий случай». В отладочной сборке React проверяет побочные
  // эффекты, прогоняя их дважды: эффект, отмена, эффект. Наблюдатель запроса отцепляется от
  // уже запущенной мутации на шаге отмены и обратно НЕ цепляется — библиотека этого не умеет.
  // Запрос при этом уходит и возвращается с ответом, а слушать его некому: окно навсегда
  // остаётся на «Считаю…». Ровно это и было видно 04.09.2026 — ответ 200 в журнале сети и
  // вечная «Считаю…» на экране, три сквозные проверки красные.
  //
  // Отложенный на такт вызов уходит уже ПОСЛЕ обеих прогонок, к живому наблюдателю. Отмена
  // таймера при этом обязательна и делает второе дело: спрашивает ровно один раз, а не два.
  const { mutate: задать } = спросить;
  useEffect(() => {
    const очищенный = (начальныйВопрос ?? '').trim();
    if (очищенный.length === 0) {
      return;
    }
    // Первый вопрос из палитры — начало разговора: прошлого у него нет по построению.
    const таймер = setTimeout(() => задать({ question: очищенный, history: [] }), 0);
    return () => clearTimeout(таймер);
  }, [начальныйВопрос, задать]);

  // ...и он же попадает в память разговора: следующая реплика («а за июнь?») уточняет именно
  // его. Отдельным действием, а не внутри `отправить`: тот вопрос задан не кнопкой.
  useEffect(() => {
    const очищенный = (начальныйВопрос ?? '').trim();
    if (очищенный.length > 0) {
      запомнить([очищенный]);
    }
  }, [начальныйВопрос]);

  const ответ = спросить.data;
  const ошибка = спросить.error as ApiError | null;
  const ничегоНеСпрашивали = ответ === undefined && !спросить.isPending && ошибка === null;

  return (
    <section className='flex flex-col gap-3'>
      <div className='flex items-center gap-2'>
        <Input.Root size='small' className='flex-1'>
          <Input.Wrapper>
            <Input.Icon as={RiSparkling2Line} />
            <Input.Input
              value={вопрос}
              onChange={(событие) => {
                задатьВопрос(событие.target.value);
                if (замечание !== null) {
                  поставитьЗамечание(null);
                }
              }}
              onKeyDown={(событие) => {
                if (событие.key === 'Enter') {
                  отправить();
                }
              }}
              aria-label='Вопрос про деньги'
              placeholder={плейсхолдер}
            />
          </Input.Wrapper>
        </Input.Root>
        <Button.Root
          variant='primary'
          mode='filled'
          size='small'
          onClick={() => отправить()}
          className='shrink-0'
          // Занятость видна, но кнопка живая: нажатие во время счёта объясняет словами, а не
          // проваливается в пустоту. Выключенная кнопка ничего не сообщает.
          aria-busy={спросить.isPending}
        >
          {спросить.isPending ? 'Считаю…' : 'Спросить'}
        </Button.Root>
      </div>

      {замечание === null ? null : (
        <p className='text-paragraph-xs text-warning-base'>{замечание}</p>
      )}

      {ничегоНеСпрашивали && (
        <div className='flex flex-col gap-2'>
          {умеет.data === undefined ? (
            подписьПокаЕдет(умеет.isPending)
          ) : умеет.data.items.length === 0 ? (
            // Пустой список — это не «примеров нет», а «спрашивать нечего»: у этой роли нет
            // прав ни на один из ответов помощника, и молчать об этом нельзя.
            <p className='text-paragraph-xs text-text-sub-600'>
              По вашим правам помощник не отвечает ни на один вопрос. Права выдаёт администратор.
            </p>
          ) : (
            <>
              {/* ⚠️ Здесь стояла строка «Спросить можно про: …», склеенная из всех описаний
                  каталога, — у финансового директора это 1185 знаков, то есть стена текста
                  первым, что он видит. Её не читают; читают кнопки под ней, и они говорят то
                  же самое живыми словами. */}
              <span className='text-paragraph-xs text-text-sub-600'>
                Нажмите пример или спросите своими словами:
              </span>
              <div className='flex flex-wrap items-center gap-2'>
                {подсказки.map((подсказка) => (
                  <button
                    key={подсказка}
                    type='button'
                    onClick={() => {
                      задатьВопрос(подсказка);
                      отправить(подсказка);
                    }}
                    className='rounded-full border border-stroke-soft-200 px-3 py-1 text-paragraph-xs text-text-sub-600 transition duration-200 ease-out hover:border-stroke-sub-300 hover:text-text-strong-950'
                  >
                    {подсказка}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {спросить.isPending ? (
        <p className='text-paragraph-sm text-text-sub-600'>Считаю по данным системы…</p>
      ) : null}

      {/* Ошибка — общим конвертом продукта, а не своей плашкой со своей кнопкой: тот же
          разбор ответа сервера (что случилось, подсказка, следующий шаг) стоит ещё в
          тридцати трёх местах, и вторая его копия расходится с первой молча. */}
      {ошибка === null || спросить.isPending ? null : (
        <ErrorAlert
          error={ошибка}
          action={
            <Button.Root variant='neutral' mode='stroke' size='xxsmall' onClick={() => отправить()}>
              Повторить
            </Button.Root>
          }
        />
      )}

      {ответ === undefined || спросить.isPending ? null : (
        <div className='flex flex-col gap-3 rounded-xl border border-stroke-soft-200 bg-bg-weak-50 px-4 py-3'>
          <p className='text-paragraph-sm text-text-strong-950'>{ответ.answer}</p>

          {ответ.facts.length === 0 ? null : (
            <dl className='flex flex-col gap-1'>
              {ответ.facts.map((величина) => (
                <div key={величина.title} className='flex items-baseline justify-between gap-4'>
                  <dt className='text-paragraph-sm text-text-sub-600'>{величина.title}</dt>
                  <dd className='text-label-sm tabular-nums text-text-strong-950'>
                    {величина.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          <div className='flex items-center justify-between gap-3'>
            {/* Кто сложил фразу — сказано прямо. Числа считает код всегда, а формулировка
                бывает машинной: молчание об этом однажды обернётся претензией «ваш робот
                сказал мне неправду». */}
            <span className='text-paragraph-xs text-text-sub-600'>
              {ответ.note ??
                (ответ.by_model
                  ? `Фразу составила модель по данным системы · ${ответ.source}`
                  : ответ.source)}
            </span>
            {ответ.link === null || ответ.link === undefined ? null : (
              <Link
                to={ответ.link}
                onClick={наПереход}
                className='flex shrink-0 items-center gap-1 text-label-xs text-primary-base'
              >
                Открыть экран
                <RiArrowRightSLine className='size-4' />
              </Link>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
